import axios from 'axios';
import Cookies from 'js-cookie';
import i18n from '../i18n';
import { installSessionInterceptors } from '../services/sessionSecurity';

installSessionInterceptors(axios);

// const base_url = import.meta.env.REACT_APP_BASE;
const DEFAULT_BACKEND_BASE_URL = 'https://washday-backend.herokuapp.com/';
const base_url = import.meta.env.REACT_APP_BACKEND_BASE_URL || DEFAULT_BACKEND_BASE_URL;
const error_status = 'error';
const success_status = 'success';
const msg_undefined = 'Something went wrong';
const MAX_USER_ERROR_LENGTH = 500;
const HTML_ERROR_PATTERN = /<!doctype\s+html|<\/?[a-z][^>]*>/i;
const STACK_TRACE_PATTERN =
  /\n\s*at\s+\S+|(?:^|\n)\s*Traceback \(most recent call last\):|(?:^|\n)\s*File "[^"]+", line \d+|(?:^|\n)\s*Stack trace:/i;
export let cancelToken = null;

const createRequestId = () => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  return `wd-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const getErrorRequestId = (error, fallbackRequestId) => {
  const responseHeaders = error?.response?.headers;

  return (
    responseHeaders?.['x-request-id'] ||
    responseHeaders?.['X-Request-Id'] ||
    responseHeaders?.get?.('x-request-id') ||
    fallbackRequestId
  );
};

const withRequestReference = (message, error, fallbackRequestId) => {
  const requestId = getErrorRequestId(error, fallbackRequestId);
  return `${message}\n${i18n.t('Notifications:requestReference', { requestId })}`;
};

const getSafeRequestPath = (url) => {
  if (typeof url !== 'string' || !url) return undefined;

  try {
    return new URL(url, base_url).pathname;
  } catch {
    return url.split(/[?#]/)[0];
  }
};

const logLegacyApiError = (error, fallbackRequestId) => {
  console.error('Legacy API request failed', {
    requestId: getErrorRequestId(error, fallbackRequestId),
    method: error?.config?.method?.toUpperCase?.(),
    path: getSafeRequestPath(error?.config?.url),
    status: error?.response?.status
  });
};

const getSafeErrorMessage = (value) => {
  if (typeof value !== 'string') return null;

  const message = value.trim();
  if (
    !message ||
    message.length > MAX_USER_ERROR_LENGTH ||
    HTML_ERROR_PATTERN.test(message) ||
    STACK_TRACE_PATTERN.test(message)
  ) {
    return null;
  }

  return message;
};

const getSafeErrorMessages = (value, translator, shouldTranslate = false) => {
  const values = Array.isArray(value) ? value : [value];

  return values
    .map((item) => {
      const rawMessage =
        item && typeof item === 'object' && !Array.isArray(item) ? item.message : item;
      const safeMessage = getSafeErrorMessage(rawMessage);

      if (!safeMessage) return null;

      const translatedMessage =
        shouldTranslate && safeMessage.includes('backend.errors')
          ? translator(safeMessage)
          : safeMessage;

      return getSafeErrorMessage(translatedMessage);
    })
    .filter(Boolean);
};

// @deprecated use washday-sdk instead or
/* query ---> api url to query with
   no_token ---> acts as a flag for no need to use token */
export const getData = async (
  query,
  no_token,
  translator = (a) => a,
  contentType = null,
  responseType = null,
  contentDisposition
) => {
  const requestId = createRequestId();

  try {
    const token = Cookies.get('token');
    // if (cancelToken) cancelToken.cancel();
    cancelToken = axios.CancelToken.source();

    const headerObj = {
      'x-request-id': requestId
    };

    if (!no_token) {
      headerObj.Authorization = `Bearer ${token}`;
    }

    if (contentType) {
      headerObj['Content-Type'] = contentType;
    }

    const params = {
      headers: headerObj
    };

    if (responseType) {
      params.responseType = responseType;
    }

    if (contentDisposition) {
      params['Content-disposition'] = contentDisposition;
    }

    params.cancelToken = cancelToken.token;

    const data = await axios.get(`${base_url}${query}`, params);
    if (checkRes(data && data.status)) {
      return data;
    }
    alertPop(error_status, msg_undefined);
  } catch (error) {
    const returnedError = errorHandle(error, translator, requestId);

    if (returnedError) {
      return returnedError;
    }

    return false;
  }
};

/* query ---> api url to query with
   data ---> data to be posted
   no_token ---> acts as a flag for no need to use token */
export const postData = async (
  query,
  data,
  no_token,
  successMessage,
  contentType = 'application/json',
  translator = (a) => a
) => {
  const requestId = createRequestId();

  try {
    const token = Cookies.get('token');
    if (cancelToken) cancelToken.cancel();
    cancelToken = axios.CancelToken.source();

    const headerObj = {
      'x-request-id': requestId
    };

    if (!no_token) {
      headerObj.Authorization = `Bearer ${token}`;
    }

    if (contentType) {
      headerObj['Content-Type'] = contentType;
    }

    const res = await axios(
      {
        method: 'post',
        url: `${base_url}${query}`,
        headers: headerObj,
        data
      },
      {
        cancelToken: cancelToken.token
      }
    );
    if (res && checkRes(res.status)) {
      if (successMessage) alertPop(success_status, successMessage);
      return res;
    }
    alertPop(error_status, msg_undefined);
  } catch (error) {
    const returnedError = errorHandle(error, translator, requestId);
    if (returnedError) {
      return returnedError;
    }

    return false;
  }
};

export const putData = async (
  query,
  data,
  no_token,
  successMessage,
  translator = (a) => a,
  showAlert = true
) => {
  const requestId = createRequestId();

  try {
    const token = Cookies.get('token');
    if (cancelToken) cancelToken.cancel();
    cancelToken = axios.CancelToken.source();

    const headerObj = {
      'x-request-id': requestId
    };

    if (!no_token) {
      headerObj.Authorization = `Bearer ${token}`;
    }

    const res = await axios(
      {
        method: 'put',
        url: `${base_url}${query}`,
        headers: headerObj,
        data
      },
      {
        cancelToken: cancelToken.token
      }
    );
    if (res && checkRes(res.status)) {
      if (successMessage && showAlert) {
        alertPop(success_status, successMessage);
      }
      return res;
    }
    if (showAlert) {
      alertPop(error_status, msg_undefined);
    }
  } catch (error) {
    const returnedError = errorHandle(error, translator, requestId);
    if (returnedError) {
      return returnedError;
    }
    return false;
  }
};

export const delData = async (query, data, no_token, successMessage, translator = (a) => a) => {
  const requestId = createRequestId();

  try {
    const token = Cookies.get('token');
    if (cancelToken) cancelToken.cancel();
    cancelToken = axios.CancelToken.source();

    const headerObj = {
      'x-request-id': requestId
    };

    if (!no_token) {
      headerObj.Authorization = `Bearer ${token}`;
    }

    const res = await axios(
      {
        method: 'delete',
        url: `${base_url}${query}`,
        headers: headerObj,
        data
      },
      {
        cancelToken: cancelToken.token
      }
    );
    if (res && checkRes(res.status)) {
      if (successMessage) alertPop(success_status, successMessage);
      return res;
    }
    alertPop(error_status, msg_undefined);
  } catch (error) {
    const returnedError = errorHandle(error, translator, requestId);
    if (returnedError) {
      return returnedError;
    }
    return false;
  }
};

const checkRes = (param) => {
  if (param === 200 || param === 201 || param === 212) {
    return true;
  }
  return false;
};

export const alertPop = (type, value) => {
  // message[type](value);
  window.alert(value);
};

const errorHandle = (error, translator = () => {}, requestId) => {
  if (axios.isCancel(error)) {
    console.log('request cancelled');
    return;
  }
  // The shared interceptor has already ended this session; alerts would block navigation.
  if (error?.response?.status === 401 && error?.config?.sessionToken) return;
  logLegacyApiError(error, requestId);
  if (error?.response?.status === 402 && error?.response?.data?.data) {
    localStorage.clear();
  }
  checkRes(error?.response?.status);

  const responseData = error?.response?.data;
  const returnOriginalError =
    Boolean(responseData?.errors) &&
    typeof responseData.errors === 'object' &&
    !Array.isArray(responseData.errors);
  const errorCandidates = [
    { value: responseData?.error, shouldTranslate: false, returnError: false },
    { value: responseData?.message, shouldTranslate: false, returnError: false },
    {
      value: responseData?.errors,
      shouldTranslate: true,
      returnError: returnOriginalError
    }
  ];

  for (const candidate of errorCandidates) {
    const messages = getSafeErrorMessages(candidate.value, translator, candidate.shouldTranslate);
    if (messages.length) {
      alertPop(error_status, withRequestReference(messages.join('\n'), error, requestId));
      return candidate.returnError ? error : undefined;
    }
  }

  if (error?.response?.status) {
    const message = i18n.t('Notifications:requestFailed', {
      status: error.response.status
    });
    alertPop(error_status, withRequestReference(message, error, requestId));
    return returnOriginalError ? error : undefined;
  }

  const message = i18n.t('Notifications:networkError');
  alertPop(error_status, withRequestReference(message, error, requestId));
  return returnOriginalError ? error : undefined;
};
